import React, { useState } from 'react'
import { Check, ExternalLink, Loader2, Package, MapPin, ThumbsUp, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { isBrandStep } from '@adthlete/common/utils/collaborationState'
import {
  useCompleteBrandStepMutation,
  useReviewSubmissionMutation,
  type Collaboration,
  type Milestone,
  type Submission,
} from '@/redux/marketerApi'

const INPUT = 'border-foreground/15 bg-card text-foreground placeholder:text-gray-500'

/** The athlete steps the brand answers right here, not on the concept/video review pages */
const INLINE_REVIEW_SLUGS = ['post_content', 'check_in', 'create_draft']

const linksOf = (s: Submission): { url: string; name?: string }[] => {
  const raw: any = s.contentUrls
  if (Array.isArray(raw)) return raw.filter((l) => l?.url)
  if (raw && Array.isArray(raw.links)) return raw.links.map((url: string) => ({ url }))
  return []
}

const current = (milestones: Milestone[]) =>
  [...milestones]
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    .find((m) => m.status !== 'approved' && m.status !== 'completed') ?? null

const Field: React.FC<{
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
}> = ({ id, label, value, onChange, placeholder, type = 'text' }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-gray-700">
      {label}
    </Label>
    <Input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={INPUT}
    />
  </div>
)

/**
 * The brand's move on a deal, right on the collaboration page: mark its own step done (ship
 * the product with tracking, send the appearance details, confirm it happened, approve the
 * draft), or answer the athlete's post, check-in or draft with Approve / Request changes.
 */
export const BrandStepCard: React.FC<{
  collaboration: Collaboration
  athleteName: string
  onDone?: () => void
}> = ({ collaboration, athleteName, onDone }) => {
  const { toast } = useToast()
  const [complete, { isLoading: completing }] = useCompleteBrandStepMutation()
  const [review, { isLoading: reviewing }] = useReviewSubmissionMutation()
  const [form, setForm] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const [changes, setChanges] = useState(false)
  const firstName = athleteName.split(' ')[0]

  if (collaboration.status !== 'accepted') return null
  const step = current(collaboration.milestones ?? [])
  if (!step || !step.slug) return null
  const slug = step.slug
  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }))

  const fail = (err: any) =>
    toast({
      title: "Couldn't save",
      description: err?.data?.error ?? 'Try again in a moment.',
      variant: 'destructive',
    })

  // ── The brand's own step ─────────────────────────────────────────────────────
  if (isBrandStep(step)) {
    const submit = async () => {
      try {
        await complete({
          collaborationId: collaboration.id,
          milestoneId: step.id,
          metadata: form,
          note: note.trim() || undefined,
        }).unwrap()
        toast({ title: `${step.name}: done`, description: `${firstName} has been told.` })
        setForm({})
        setNote('')
        onDone?.()
      } catch (err) {
        fail(err)
      }
    }
    const ready =
      slug === 'product_shipped'
        ? !!form.tracking?.trim()
        : slug === 'prepare_appearance'
          ? !!form.location?.trim() && !!form.starts?.trim()
          : true

    return (
      <section className="rounded-xl border border-[#C0FD72]/40 bg-[#C0FD72]/10 p-5">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
          {slug === 'product_shipped' ? (
            <Package className="h-4 w-4" />
          ) : slug === 'prepare_appearance' ? (
            <MapPin className="h-4 w-4" />
          ) : (
            <ThumbsUp className="h-4 w-4" />
          )}
          Your move
        </p>
        <h2 className="mt-1 text-lg font-semibold text-foreground">{step.name}</h2>
        <p className="mt-1 text-sm text-gray-700">
          {slug === 'product_shipped'
            ? `Ship the product to ${firstName}, then add the tracking so they can follow it.`
            : slug === 'prepare_appearance'
              ? `Tell ${firstName} where to be and when. They check in on the day.`
              : slug === 'brand_confirms'
                ? `${firstName} checked in. Confirm the appearance happened to release payment.`
                : slug === 'brand_approves'
                  ? `Approve ${firstName}'s draft so they can post it.`
                  : step.description}
        </p>

        {slug === 'product_shipped' ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Field
              id="carrier"
              label="Carrier"
              value={form.carrier ?? ''}
              onChange={set('carrier')}
              placeholder="UPS"
            />
            <Field
              id="tracking"
              label="Tracking number"
              value={form.tracking ?? ''}
              onChange={set('tracking')}
              placeholder="1Z…"
            />
            <Field
              id="arrives"
              label="Arrives"
              type="date"
              value={form.arrives ?? ''}
              onChange={set('arrives')}
            />
          </div>
        ) : slug === 'prepare_appearance' ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field
              id="location"
              label="Where"
              value={form.location ?? ''}
              onChange={set('location')}
              placeholder="SportFuel store, 120 State St"
            />
            <Field
              id="starts"
              label="When"
              type="datetime-local"
              value={form.starts ?? ''}
              onChange={set('starts')}
            />
            <Field
              id="hours"
              label="How long"
              value={form.hours ?? ''}
              onChange={set('hours')}
              placeholder="2 hours"
            />
            <Field
              id="contact"
              label="Who to ask for"
              value={form.contact ?? ''}
              onChange={set('contact')}
              placeholder="Dana, store manager"
            />
          </div>
        ) : null}

        <div className="mt-4 space-y-1.5">
          <Label htmlFor="step-note" className="text-gray-700">
            Note to {firstName} (optional)
          </Label>
          <Textarea
            id="step-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            className={INPUT}
          />
        </div>
        <Button variant="lime" className="mt-4" disabled={!ready || completing} onClick={submit}>
          {completing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {slug === 'product_shipped'
            ? 'Mark as shipped'
            : slug === 'prepare_appearance'
              ? 'Send the details'
              : slug === 'brand_confirms'
                ? 'Confirm the appearance'
                : slug === 'brand_approves'
                  ? 'Approve the draft'
                  : 'Mark done'}
        </Button>
      </section>
    )
  }

  // ── The athlete's post / check-in / draft, answered here ─────────────────────
  const pending = (step.submissions ?? []).find((s) => s.status === 'pending_review')
  if (!pending || !INLINE_REVIEW_SLUGS.includes(slug)) return null
  const links = linksOf(pending)

  const answer = async (action: 'approve' | 'request_changes') => {
    try {
      await review({
        collaborationId: collaboration.id,
        milestoneId: step.id,
        action,
        reviewNotes: note.trim() || undefined,
      }).unwrap()
      toast({
        title: action === 'approve' ? `${step.name}: approved` : `Changes requested`,
        description: `${firstName} has been told.`,
      })
      setNote('')
      setChanges(false)
      onDone?.()
    } catch (err) {
      fail(err)
    }
  }

  return (
    <section className="rounded-xl border border-[#C0FD72]/40 bg-[#C0FD72]/10 p-5">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
        <ThumbsUp className="h-4 w-4" />
        Review · your move
      </p>
      <h2 className="mt-1 text-lg font-semibold text-foreground">
        {firstName}{' '}
        {slug === 'check_in' ? 'checked in' : slug === 'create_draft' ? 'sent a draft' : 'posted'}
      </h2>
      {pending.notes ? (
        <p className="mt-2 text-sm leading-relaxed text-gray-700">“{pending.notes}”</p>
      ) : null}
      {links.length ? (
        <ul className="mt-3 space-y-1">
          {links.map((l) => (
            <li key={l.url}>
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#3F7A0A] underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {l.name ?? l.url}
              </a>
            </li>
          ))}
        </ul>
      ) : null}

      {changes ? (
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="review-note" className="text-gray-700">
            What should {firstName} change?
          </Label>
          <Textarea
            id="review-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className={INPUT}
          />
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {changes ? (
          <>
            <Button
              variant="dark-outline"
              disabled={reviewing || note.trim().length < 3}
              onClick={() => answer('request_changes')}
            >
              {reviewing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Undo2 className="h-4 w-4" />
              )}
              Send changes
            </Button>
            <Button variant="ghost" className="text-gray-700" onClick={() => setChanges(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button variant="lime" disabled={reviewing} onClick={() => answer('approve')}>
              {reviewing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Approve
            </Button>
            <Button variant="dark-outline" disabled={reviewing} onClick={() => setChanges(true)}>
              <Undo2 className="h-4 w-4" />
              Request changes
            </Button>
          </>
        )}
      </div>
    </section>
  )
}

export default BrandStepCard
